import { AnimatePresence, motion } from "framer-motion";
import { X, DollarSign, TrendingUp, FileText, GraduationCap, Bell, CheckCheck, AlertCircle } from "lucide-react";
import { useNotifications, useMarkNotificationsRead, type AppNotification } from "@/hooks/useNotifications";

type IconConfig = { icon: React.ElementType; bg: string; color: string }

const TYPE_META: Record<string, IconConfig> = {
  market_price:       { icon: TrendingUp,    bg: "bg-amber-50",  color: "text-amber-700"  },
  investment_update:  { icon: DollarSign,    bg: "bg-green-50",  color: "text-green-700"  },
  match_found:        { icon: TrendingUp,    bg: "bg-amber-50",  color: "text-amber-700"  },
  contract_signed:    { icon: FileText,      bg: "bg-blue-50",   color: "text-blue-700"   },
  escrow_funded:      { icon: DollarSign,    bg: "bg-green-50",  color: "text-green-700"  },
  delivery_confirmed: { icon: CheckCheck,    bg: "bg-green-50",  color: "text-green-700"  },
  kyc_approved:       { icon: CheckCheck,    bg: "bg-green-50",  color: "text-green-700"  },
  kyc_rejected:       { icon: AlertCircle,   bg: "bg-red-50",    color: "text-red-600"    },
  payment_received:   { icon: DollarSign,    bg: "bg-green-50",  color: "text-green-700"  },
  report_submitted:   { icon: FileText,      bg: "bg-blue-50",   color: "text-blue-700"   },
  invoice_issued:     { icon: FileText,      bg: "bg-amber-50",  color: "text-amber-700"  },
  course:             { icon: GraduationCap, bg: "bg-purple-50", color: "text-purple-700" },
  system:             { icon: Bell,          bg: "bg-gray-100",  color: "text-gray-600"   },
}
const DEFAULT_META: IconConfig = { icon: Bell, bg: "bg-gray-100", color: "text-gray-600" }

function formatDate(iso: string): string {
  const d = new Date(iso)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)
  if (d.toDateString() === today.toDateString()) return "Aujourd'hui"
  if (d.toDateString() === yesterday.toDateString()) return "Hier"
  return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export function NotificationPanel({ open, onClose }: Props) {
  const { data } = useNotifications()
  const markRead = useMarkNotificationsRead()

  const items: AppNotification[] = data?.notifications ?? []
  const unread = data?.unreadCount ?? 0

  function handleMarkAllRead() {
    markRead.mutate([])
  }

  function handleMarkRead(id: string) {
    markRead.mutate([id])
  }

  const grouped: Record<string, AppNotification[]> = {}
  items.forEach((n) => {
    const label = formatDate(n.createdAt)
    if (!grouped[label]) grouped[label] = []
    grouped[label].push(n)
  })

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="notif-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black/30"
            onClick={onClose}
          />

          <motion.div
            key="notif-panel"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 380, damping: 36 }}
            className="fixed top-0 right-0 bottom-0 z-50 w-full max-w-sm bg-white shadow-2xl flex flex-col"
          >
            <div className="flex items-center justify-between px-5 pt-6 pb-4 border-b border-gray-100">
              <div>
                <h2 className="font-display font-black text-[18px] text-gray-900">Notifications</h2>
                {unread > 0 && (
                  <p className="text-[12px] text-gray-500 mt-0.5">{unread} non lue{unread > 1 ? "s" : ""}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {unread > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    className="h-8 px-3 rounded-lg bg-gray-100 hover:bg-gray-200 text-[11px] font-bold text-gray-600 flex items-center gap-1.5 transition"
                  >
                    <CheckCheck className="w-3.5 h-3.5" /> Tout lire
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition"
                >
                  <X className="w-4 h-4 text-gray-600" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {Object.entries(grouped).map(([date, group]) => (
                <div key={date}>
                  <p className="px-5 pt-4 pb-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                    {date}
                  </p>
                  {group.map((n) => {
                    const meta = TYPE_META[n.type] ?? DEFAULT_META
                    const Icon = meta.icon
                    return (
                      <button
                        key={n.id}
                        onClick={() => handleMarkRead(n.id)}
                        className={`w-full flex items-start gap-3 px-5 py-3.5 text-left hover:bg-gray-50 transition ${!n.read ? "bg-green-50/40" : ""}`}
                      >
                        <div className={`w-10 h-10 rounded-xl ${meta.bg} ${meta.color} flex items-center justify-center shrink-0 mt-0.5`}>
                          <Icon className="w-4.5 h-4.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-[13px] font-bold text-gray-900 leading-tight ${!n.read ? "font-extrabold" : ""}`}>
                              {n.title}
                            </p>
                            <span className="text-[10px] text-gray-400 shrink-0 mt-0.5">{formatTime(n.createdAt)}</span>
                          </div>
                          <p className="text-[12px] text-gray-500 mt-0.5 leading-snug">{n.body}</p>
                        </div>
                        {!n.read && (
                          <div className="w-2 h-2 bg-green-600 rounded-full shrink-0 mt-2" />
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}

              {items.length === 0 && (
                <div className="flex flex-col items-center justify-center h-48 text-gray-400">
                  <Bell className="w-10 h-10 mb-3 opacity-30" />
                  <p className="text-[13px] font-semibold">Aucune notification</p>
                </div>
              )}
            </div>

            <div className="border-t border-gray-100 px-5 py-4">
              <p className="text-[11px] text-center text-gray-400">
                Les notifications sont conservées 30 jours
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
